import React, { useState, useEffect, useCallback } from "react";
import { Table, Button, Pagination, Spinner, Alert, Modal } from "react-bootstrap";
import ReportService from "../services/report.service";
import { toast } from "react-toastify";
import EventBus from "../common/EventBus";

const ShopBlackListManager = () => {
    const [shops, setShops] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [totalPages, setTotalPages] = useState(0);
    const pageSize = 50;

    // Confirmation Modal State
    const [showConfirm, setShowConfirm] = useState(false);
    const [confirmShopName, setConfirmShopName] = useState('');

    const fetchShops = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await ReportService.getBlackListShops(searchTerm, page, pageSize);
            setShops(response.data.content);
            setTotalPages(response.data.totalPages);
        } catch (err) {
            console.error("Error fetching blacklist:", err);
            setError("Failed to load blacklisted shops.");
        } finally {
            setLoading(false);
        }
    }, [page, searchTerm]);

    useEffect(() => {
        fetchShops();
    }, [fetchShops]);

    const handleDeleteClick = (shopName) => {
        setConfirmShopName(shopName);
        setShowConfirm(true);
    };

    const handleConfirmDelete = async () => {
        setShowConfirm(false);
        setLoading(true);

        try {
            await ReportService.removeFromBlackList(confirmShopName);
            toast.success(`"${confirmShopName}" removed from Black List`);
            fetchShops();
        } catch (err) {
            console.error(`Error deleting from blacklist:`, err);
            EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: `Failed to remove "${confirmShopName}" from Black List.` });
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    if (error) {
        return <Alert variant="danger" className="mt-3">{error}</Alert>;
    }

    return (
        <div className="container-fluid py-4" style={{ background: 'var(--bg-gradient)', minHeight: 'calc(100vh - 76px)' }}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>Shop Black List Management</h2>
                <div className="d-flex align-items-center gap-3">
                    <div className="input-group" style={{ width: '300px' }}>
                        <span className="input-group-text bg-white border-end-0">
                            <i className="bi bi-search text-muted"></i>
                        </span>
                        <input 
                            type="text" 
                            className="form-control border-start-0" 
                            placeholder="Search shop name..." 
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setPage(0); // Reset to first page on search
                            }}
                        />
                    </div>
                    {loading && <Spinner animation="border" size="sm" />}
                </div>
            </div>

            <Table striped bordered hover responsive className="shadow-sm">
                <thead className="table-dark">
                    <tr>
                        <th>Shop Name</th>
                        <th style={{ width: '150px' }}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {shops.length > 0 ? (
                        shops.map((shop, index) => (
                            <tr key={index}>
                                <td className="align-middle fw-bold">{shop}</td>
                                <td className="align-middle text-center">
                                    <Button 
                                        variant="outline-danger" 
                                        size="sm"
                                        onClick={() => handleDeleteClick(shop)}
                                    >
                                        <i className="bi bi-trash me-1"></i> Delete
                                    </Button>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="2" className="text-center py-4 text-muted">
                                {loading ? "Loading..." : "No blacklisted shops found."}
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>

            {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                    <Pagination>
                        <Pagination.First onClick={() => handlePageChange(0)} disabled={page === 0} />
                        <Pagination.Prev onClick={() => handlePageChange(page - 1)} disabled={page === 0} />
                        {[...Array(totalPages).keys()].map((p) => {
                            if (p < page - 2 || p > page + 2) return null;
                            return (
                                <Pagination.Item 
                                    key={p} 
                                    active={p === page}
                                    onClick={() => handlePageChange(p)}
                                >
                                    {p + 1}
                                </Pagination.Item>
                            );
                        })}
                        <Pagination.Next onClick={() => handlePageChange(page + 1)} disabled={page === totalPages - 1} />
                        <Pagination.Last onClick={() => handlePageChange(totalPages - 1)} disabled={page === totalPages - 1} />
                    </Pagination>
                </div>
            )}

            {/* Confirmation Modal */}
            <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
                <Modal.Header closeButton className="bg-danger text-white">
                    <Modal.Title>Confirm Removal</Modal.Title>
                </Modal.Header>
                <Modal.Body className="py-4 text-center">
                    <p className="mb-0">Are you sure you want to remove <strong>"{confirmShopName}"</strong> from the Black List?</p>
                    <p className="text-muted small mt-2">Workstations reporting this shop name will no longer be automatically blocked.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirm(false)}>Cancel</Button>
                    <Button variant="danger" onClick={handleConfirmDelete}>
                        Confirm Delete
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ShopBlackListManager;
